import type { AsFirebasePayload, AuthKey, FProject, Projects } from "@piperunner-cms/types/v1";
import { collection, doc, getDoc, getDocs, query, setDoc, where } from "firebase/firestore";
import { FIRESTORE_COLLECTION_PATH } from "@/constants";
import { NotFoundError } from "@/constants/errors";
import { fsDb } from "../utils/initialize-fs-db";

/**
 * Fetches all projects for a given user from Firestore.
 *
 * @param {string} userId - The ID of the user whose projects are to be fetched.
 * @returns {Promise<AsFirebasePayload<Project>[]>} A promise that resolves to an array of projects.
 * @throws {Error} If no projects are found for the given userId.
 *
 * Note: For every user, there is a guaranteed project, so in case of error
 * we should try and refetch.
 */
export const getAllProjects = async (userId: string): Promise<Projects> => {
  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.PROJECTS);
  const docQuery = query(collectionRef, where("userId", "==", userId));
  const projectsSnapshot = await getDocs(docQuery);

  if (projectsSnapshot.empty) {
    throw new NotFoundError(`No projects found for userId: ${userId}`);
  }

  const projects = projectsSnapshot.docs.reduce<Projects>((acc, doc) => {
    acc[doc.id] = {
      id: doc.id,
      payload: {
        id: doc.id,
        userId: doc.data().userId,
        name: doc.data().name,
        description: doc.data().description ?? null,
        authKeys: doc.data().authKeys,
      },
    };
    return acc;
  }, {});

  return projects;
};

/**
 * Creates a new project for a given user in Firestore.
 * @param userId - The ID of the user for whom the project is being created.
 * @param data - The project data excluding projectId and userId.
 * @returns {Promise<Project>} A promise that resolves to the newly created project.
 * @throws {Error} If the project creation fails.
 */
export const createNewProject = async (
  userId: string,
  data: Pick<FProject, "name" | "description">,
): Promise<AsFirebasePayload<FProject>> => {
  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.PROJECTS);
  const newProjectRef = doc(collectionRef);

  if (!newProjectRef.id) {
    throw new Error("Failed to create a new project.");
  }

  const newProjectData: FProject = {
    ...data,
    id: newProjectRef.id,
    userId,
    authKeys: [],
  };

  await setDoc(newProjectRef, newProjectData);

  return {
    id: newProjectRef.id,
    payload: {
      ...newProjectData,
    },
  };
};

/**
 * Updates an existing project in Firestore.
 * @param id - The ID of the project to be updated.
 * @param data - The data to update the project with, excluding projectId and userId.
 * @returns {Promise<AsFirebasePayload<Project>>} A promise that resolves to the updated project.
 * @throws {NotFoundError} If the project with the given ID does not exist.
 */
export const updateProject = async (
  id: string,
  data: Partial<Pick<FProject, "description" | "name">>,
): Promise<AsFirebasePayload<FProject>> => {
  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.PROJECTS);
  const projectRef = doc(collectionRef, id);

  const projectSnapshot = await getDoc(projectRef);
  if (!projectSnapshot.exists()) {
    throw new NotFoundError(`Project with ID ${id} not found.`);
  }

  const projectData = projectSnapshot.data() as FProject;
  const updatedProjectData: FProject = {
    ...projectData,
    ...data,
  };
  await setDoc(projectRef, updatedProjectData, {
    merge: true, // Use merge to update only the fields provided in data
  });
  return {
    id,
    payload: updatedProjectData,
  };
};

/**
 * Fetches all auth keys for a given project.
 * @param projectId - The ID of the project for which the auth keys are to be fetched.
 * @param data - The data to create a new auth key, including the name.
 * @returns {Promise<AsFirebasePayload<AuthKey>>} A promise that resolves to the newly created auth key.
 * @throws {NotFoundError} If the project with the given ID does not exist.
 * @throws {Error} If the auth key creation fails.
 */
export const createNewAuthKey = async (
  projectId: string,
  data: Pick<AuthKey, "name">,
): Promise<AsFirebasePayload<AuthKey>> => {
  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.PROJECTS);
  const projectRef = doc(collectionRef, projectId);

  const projectSnapshot = await getDoc(projectRef);

  if (!projectSnapshot.exists()) {
    throw new NotFoundError(`Project with ID ${projectId} not found.`);
  }

  const projectData = projectSnapshot.data() as FProject;

  const newAuthKey: AuthKey = {
    name: data.name,
    value: crypto.randomUUID(), // Generate a unique value for the auth key
    enabled: true,
  };

  if (!projectData.authKeys || !Array.isArray(projectData.authKeys)) {
    projectData.authKeys = [];
  }

  projectData.authKeys.push(newAuthKey);

  await setDoc(projectRef, projectData);

  return {
    id: newAuthKey.value,
    payload: newAuthKey,
  };
};

/**
 * Deletes an auth key from a project.
 * @param projectId - The ID of the project from which the auth key will be deleted.
 * @param authKeyValue - The value of the auth key to be deleted.
 */
export const deleteAuthKey = async (projectId: string, authKeyValue: string) => {
  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.PROJECTS);
  const projectRef = doc(collectionRef, projectId);

  const projectSnapshot = await getDoc(projectRef);

  if (!projectSnapshot.exists()) {
    throw new NotFoundError(`Project with ID ${projectId} not found.`);
  }

  const projectData = projectSnapshot.data() as FProject;

  const authKeyIndex = projectData.authKeys.findIndex((key) => key.value === authKeyValue);

  if (authKeyIndex === -1) {
    throw new NotFoundError(`Auth key with value ${authKeyValue} not found in project ${projectId}.`);
  }

  projectData.authKeys.splice(authKeyIndex, 1);

  await setDoc(projectRef, projectData);
};

/**
 * Updates an existing auth key in a project.
 * @param projectId - The ID of the project containing the auth key to be updated.
 * @param authKeyValue - The value of the auth key to be updated.
 * @param data - The data to update the auth key with, excluding the value.
 * @returns {Promise<AsFirebasePayload<AuthKey>>} A promise that resolves to the updated auth key.
 * @throws {NotFoundError} If the project or auth key is not found.
 */
export const updateAuthKey = async (projectId: string, authKeyValue: string, data: Exclude<AuthKey, "value">) => {
  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.PROJECTS);
  const projectRef = doc(collectionRef, projectId);

  const projectSnapshot = await getDoc(projectRef);

  if (!projectSnapshot.exists()) {
    throw new NotFoundError(`Project with ID ${projectId} not found.`);
  }

  const projectData = projectSnapshot.data() as FProject;

  const authKeyIndex = projectData.authKeys.findIndex((key) => key.value === authKeyValue);

  if (authKeyIndex === -1) {
    throw new NotFoundError(`Auth key with value ${authKeyValue} not found in project ${projectId}.`);
  }

  // Update the auth key with the provided data
  projectData.authKeys[authKeyIndex] = {
    ...projectData.authKeys[authKeyIndex],
    ...data,
  };

  await setDoc(projectRef, projectData);

  return {
    id: authKeyValue,
    payload: projectData.authKeys[authKeyIndex],
  };
};
